// CTSK-509 — inline note editor: a textarea, the five colour swatches and
// Save / Cancel, with a Write / Preview toggle (markdown). No modal. Esc cancels, Cmd/Ctrl+Enter saves. A character
// counter appears once the body passes NOTE_COUNTER_THRESHOLD (3500 of 4000).

import { useState, type KeyboardEvent } from 'react';
import {
  NOTE_BODY_MAX,
  NOTE_COLOR_PALETTE,
  NOTE_COUNTER_THRESHOLD,
  NOTE_DEFAULT_COLOR,
  type NoteColor,
} from '@captain-tasko/shared';
import { NoteMarkdown } from './NoteMarkdown.js';
import './notes.css';

export interface NoteEditorProps {
  initialBody?: string;
  initialColor?: NoteColor;
  saveLabel?: string;
  isSaving?: boolean;
  error?: string | null;
  /** Never open shorter than this (px); an existing note keeps its card height. */
  minHeight?: number;
  onSave: (draft: { body: string; color: NoteColor }) => void;
  onCancel: () => void;
}

export function NoteEditor({
  initialBody = '',
  initialColor = NOTE_DEFAULT_COLOR,
  saveLabel = 'Save',
  isSaving = false,
  error = null,
  minHeight = 0,
  onSave,
  onCancel,
}: NoteEditorProps) {
  const [body, setBody] = useState(initialBody);
  const [color, setColor] = useState<NoteColor>(initialColor);
  const [tab, setTab] = useState<'write' | 'preview'>('write');

  const length = body.length;
  const blank = body.trim().length === 0;
  const tooLong = length > NOTE_BODY_MAX;
  const canSave = !blank && !tooLong && !isSaving;

  function save() {
    if (canSave) onSave({ body, color });
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      save();
    }
  }

  return (
    <div
      className={`note-editor note-card--${color}`}
      data-testid="note-editor"
      style={minHeight > 0 ? { minHeight } : undefined}
    >
      <div className="note-editor__tabs" role="tablist" aria-label="Note mode">
        <button type="button" role="tab" aria-selected={tab === 'write'} className={`note-editor__tab${tab === 'write' ? ' note-editor__tab--on' : ''}`} onClick={() => setTab('write')}>Write</button>
        <button type="button" role="tab" aria-selected={tab === 'preview'} className={`note-editor__tab${tab === 'preview' ? ' note-editor__tab--on' : ''}`} onClick={() => setTab('preview')}>Preview</button>
        <span className="note-editor__hint">Markdown supported</span>
      </div>
      {tab === 'write' ? (
        <textarea
          className="note-editor__textarea"
          aria-label="Note text"
          value={body}
          autoFocus
          rows={3}
          placeholder="Write a note… (Markdown supported)"
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
        />
      ) : (
        <div className="note-editor__preview" data-testid="note-preview">
          {blank ? <span className="note-editor__hint">Nothing to preview.</span> : <NoteMarkdown source={body} />}
        </div>
      )}
      <div className="note-editor__row">
        <div className="note-editor__swatches" role="radiogroup" aria-label="Note colour">
          {NOTE_COLOR_PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={color === c}
              aria-label={c}
              title={c}
              className={`note-swatch note-swatch--${c}${color === c ? ' note-swatch--selected' : ''}`}
              onClick={() => setColor(c)}
            />
          ))}
        </div>
        {length > NOTE_COUNTER_THRESHOLD && (
          <span
            className={`note-editor__counter${tooLong ? ' note-editor__counter--over' : ''}`}
            data-testid="note-counter"
            aria-live="polite"
          >
            {length}/{NOTE_BODY_MAX}
          </span>
        )}
        <span className="note-editor__spacer" />
        <button type="button" className="btn btn--ghost" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn" disabled={!canSave} onClick={save}>
          {isSaving ? 'Saving…' : saveLabel}
        </button>
      </div>
      {error && <div role="alert" className="note-editor__error">{error}</div>}
    </div>
  );
}
